import { useAccountRepository } from '@frontend/repository';
import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { LuForward } from 'react-icons/lu';
import { useIntl } from 'react-intl';

export interface ForeignEntityMarkerProps {
    /** True when the entity belongs to an account other than the requesting user's. */
    foreign?: boolean;
    /** Account id of the entity's owning account — used to resolve the hover label. */
    accountId?: string | null;
    /** Explicit account name for the hover label (wins over lookup by `accountId`). */
    accountName?: string | null;
    /**
     * Mirrored LuForward = incoming / foreign (default).
     * Set `false` for outgoing `shared_with` (normal forward arrow).
     */
    mirrored?: boolean;
    className?: string;
}

/**
 * Reads owning account id from a ResolvedEntity-like object.
 * Prefers top-level `account_id` (API entity badge), then `data.account_id`.
 */
export function accountIdFromResolvedEntity(entity: { account_id?: unknown; data?: { account_id?: unknown } } | null | undefined): string | undefined {
    if (typeof entity?.account_id === 'string' && entity.account_id.length > 0) {
        return entity.account_id;
    }
    return typeof entity?.data?.account_id === 'string' ? entity.data.account_id : undefined;
}

/** Fixed + body portal — avoids clipped/stacked tooltips inside truncated badge cells. */
const FOREIGN_MARKER_POPPER_CONFIG = {
    strategy: 'fixed' as const,
    modifiers: [
        {
            name: 'preventOverflow',
            options: { boundary: 'viewport', padding: 8 }
        },
        {
            name: 'offset',
            options: { offset: [0, 6] }
        }
    ]
};

const DEFAULT_MARKER_CLASS =
    'bg-background-surface/50 text-brand-muted hover:bg-brand-primary hover:text-background-surface inline-flex size-4 shrink-0 cursor-help items-center justify-center rounded-full';

/**
 * Corner reverse-LuForward for entities that belong to another account.
 * Parent badge must be `relative` with end padding. Absolute positioning is on an outer
 * wrapper so OverlayTrigger does not collapse an absolute trigger child.
 */
export const ForeignEntityMarker = (props: ForeignEntityMarkerProps) => {
    const intl = useIntl();
    const {
        store: { availableAccountsInfo }
    } = useAccountRepository();

    const fallbackLabel = intl.formatMessage({
        id: 'Objects.ACCOUNT.OTHER_ACCOUNT',
        description: 'Tooltip for the share marker on an entity that belongs to another account',
        defaultMessage: 'Other account'
    });

    const lookedUpName = props.accountId ? availableAccountsInfo.find((account) => account.id === props.accountId)?.name : undefined;
    const tooltipLabel = props.accountName || lookedUpName || fallbackLabel;

    if (!props.foreign) {
        return null;
    }

    return (
        <span
            data-testid='foreign-entity-marker-anchor'
            className='pointer-events-auto absolute top-0 right-0 z-10 leading-none'>
            <OverlayTrigger
                placement='top'
                delay={{ show: 200, hide: 100 }}
                container={typeof document !== 'undefined' ? document.body : undefined}
                popperConfig={FOREIGN_MARKER_POPPER_CONFIG}
                overlay={
                    <Tooltip
                        id='foreign-entity-marker-tooltip'
                        className='border-border-default bg-background-surface z-1000 flex max-w-[min(20rem,calc(100vw-1rem))] flex-col rounded-2xl border'>
                        <div
                            data-testid='foreign-entity-marker-overlay'
                            className='text-foreground-default p-4 text-sm font-medium'>
                            {tooltipLabel}
                        </div>
                    </Tooltip>
                }>
                <span
                    data-testid='foreign-entity-marker'
                    role='img'
                    aria-label={tooltipLabel}
                    className={props.className ?? DEFAULT_MARKER_CLASS}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}>
                    <LuForward
                        aria-hidden
                        className={`size-2.5 ${props.mirrored === false ? '' : '-scale-x-100'}`}
                    />
                </span>
            </OverlayTrigger>
        </span>
    );
};
